import { queryOptions } from "@tanstack/react-query";
import {
  SwitchboardResponse,
  SwitchboardHealthResponse,
  type SwitchboardFilters,
} from "@t3tools/contracts";
import { Schema } from "effect";
import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";

async function fetchSwitchboard(
  filters: SwitchboardFilters,
  page: number,
  path = "",
): Promise<unknown> {
  const params = new URLSearchParams({ page: String(page) });
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/switchboard${path}?${params}`,
    { credentials: "include" },
  );
  if (response.status === 401) throwOperatorsAuthExpired();
  if (response.status === 403)
    throw new OperatorsForbiddenError("You do not have access to Dashboards.");
  if (!response.ok)
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Switchboard dashboard",
      ),
    );
  return response.json();
}

export async function fetchSwitchboardSnapshot(
  filters: SwitchboardFilters,
  page = 1,
): Promise<SwitchboardResponse> {
  const body = await fetchSwitchboard(filters, page);
  if (!Schema.is(SwitchboardResponse)(body)) throw new Error("Switchboard response was malformed");
  return body;
}

export function switchboardSnapshotQueryOptions(filters: SwitchboardFilters = {}, page = 1) {
  return queryOptions({
    queryKey: [...operatorsQueryKeys.all, "dashboards", "switchboard", filters, page] as const,
    queryFn: () => fetchSwitchboardSnapshot(filters, page),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

// Only the shared scope applies to operational health; impact detail filters must not hide failures.
export function switchboardHealthFilters(filters: SwitchboardFilters): SwitchboardFilters {
  const scope: SwitchboardFilters = {};
  for (const key of ["dateStart", "dateEnd", "organizationId", "navigatorId"] as const) {
    if (filters[key]) scope[key] = filters[key];
  }
  return scope;
}
export async function fetchSwitchboardHealth(
  filters: SwitchboardFilters,
  page = 1,
): Promise<SwitchboardHealthResponse> {
  const body = await fetchSwitchboard(switchboardHealthFilters(filters), page, "/health");
  if (!Schema.is(SwitchboardHealthResponse)(body))
    throw new Error("Switchboard health response was malformed");
  return body;
}
export function switchboardHealthQueryOptions(filters: SwitchboardFilters = {}, page = 1) {
  const scope = switchboardHealthFilters(filters);
  return queryOptions({
    queryKey: [
      ...operatorsQueryKeys.all,
      "dashboards",
      "switchboard",
      "health",
      scope,
      page,
    ] as const,
    queryFn: () => fetchSwitchboardHealth(scope, page),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
